import { useCallback } from 'react'

import { useQuery } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import {
  libraryDepartmentQueryKeys,
  libraryDepartmentsQueryKeys,
} from '@/hooks/library/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { EntityCompanyDepartment } from '@/types/library/Entity'

interface UseCompanyDepartmentsArgs<T> {
  orgId: UUID | ''
  companyId: UUID | ''
  select?: (data: PaginatedResponse<EntityCompanyDepartment>) => T
}

const fetchDepartments = async (
  orgId: UUID | '',
  companyId: UUID | '',
  accessToken: string,
): Promise<PaginatedResponse<EntityCompanyDepartment>> => {
  if (!orgId) {
    throw new Error('orgId is required')
  }
  if (!companyId) {
    throw new Error('companyId is required')
  }
  const url = new URL(`${API_URL}/api/v1/organizations/${orgId}/entities`)
  url.searchParams.append('entity_types', 'department')
  url.searchParams.append('entity_company_id', String(companyId))

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  })
  if (!response.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: response.statusText,
      cause: response,
    })
  }
  return (await response.json()) as PaginatedResponse<EntityCompanyDepartment>
}

export const useCompanyDepartments = <T = EntityCompanyDepartment[]>({
  orgId,
  companyId,
  select,
}: UseCompanyDepartmentsArgs<T>) => {
  const { accessToken } = useAuthInfoWithThrow()

  const selectFn = useCallback(
    (data: PaginatedResponse<EntityCompanyDepartment>) => {
      if (select) {
        return select(data)
      }
      return data.data
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name)) as unknown as T
    },
    [select],
  )

  return useQuery({
    queryKey: libraryDepartmentsQueryKeys.company({ orgId, companyId }),
    queryFn: () => fetchDepartments(orgId, companyId, accessToken),
    enabled: !!orgId && !!companyId && !!accessToken,
    staleTime: Infinity,
    select: selectFn,
  })
}

interface UseDepartmentArgs {
  orgId: UUID | ''
  companyId: UUID | ''
  departmentId: UUID | ''
}

const fetchDepartment = async (
  departmentId: UUID | '',
  accessToken: string,
): Promise<EntityCompanyDepartment> => {
  if (!departmentId) {
    throw new Error('departmentId is required')
  }
  const response = await fetch(`${API_URL}/api/v1/entities/${departmentId}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  })
  if (!response.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: response.statusText,
      cause: response,
    })
  }
  return (await response.json()) as EntityCompanyDepartment
}

export const useDepartment = ({
  orgId,
  companyId,
  departmentId,
}: UseDepartmentArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  return useQuery({
    queryKey: libraryDepartmentQueryKeys.department({
      orgId,
      companyId,
      departmentId,
    }),
    queryFn: () => fetchDepartment(departmentId, accessToken),
    enabled: !!orgId && !!companyId && !!departmentId && !!accessToken,
    staleTime: Infinity,
  })
}
